import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import { useFinished } from '../../lib/practical.js';

/**
 * Junior practical for "Traps, Tricks and Nasty Software".
 *
 * The Ethical Hacker inbox asks a learner to read headers. This one
 * cannot - it is for seven to twelve year olds - so instead it asks
 * them to find the tell, by clicking it.
 *
 * Each message has one or more clickable clues buried in it, and the
 * message cannot be judged until they have been found. That way the
 * child practises looking rather than guessing, and the reveal names
 * the trick rather than just saying "wrong".
 *
 * Two of the six are completely safe, and the closing text makes a
 * point of that. A child who learns to suspect everything has learned
 * the wrong lesson and will stop telling anybody anything.
 */

const STORE_KEY = 'cybercub.lab.trap-spotter';

/*
 * Each message is a list of chunks. A chunk with `clue` is clickable
 * and is one of the things worth noticing.
 */
const MESSAGES = [
  {
    id: 'm1',
    from: 'Free Robux Giveaway',
    where: 'a message in a game',
    icon: '🎮',
    chunks: [
      { t: 'WINNER!!! ' },
      { t: 'You have been chosen for 10,000 FREE ROBUX', clue: 'toogood', tell: 'Free things that are too good to be true' },
      { t: '. Just tell us your ' },
      { t: 'username and password', clue: 'password', tell: 'Asking for your password — nobody real ever does' },
      { t: ' to claim. Offer ends in ' },
      { t: '10 minutes', clue: 'hurry', tell: 'Rushing you, so you do not stop and think' },
      { t: '!' },
    ],
    trap: true,
    why: 'Three tells in one short message. Real games never ask for your password, free things that big are never real, and a countdown is there to stop you thinking.',
  },
  {
    id: 'm2',
    from: 'Mum',
    where: 'a text message',
    icon: '💬',
    chunks: [
      { t: 'Running 10 minutes late picking you up. Wait inside with Mrs Patel. Love you x' },
    ],
    trap: false,
    why: 'Nothing to click, nothing to hurry about, nothing asked for. Most messages are exactly like this — ordinary.',
  },
  {
    id: 'm3',
    from: 'Netfl1x',
    where: 'an email',
    icon: '📧',
    chunks: [
      { t: 'Your account is locked. Click ' },
      { t: 'this link', clue: 'link', tell: 'A link that wants you to sign in somewhere' },
      { t: ' and sign in to unlock it. Sent from ' },
      { t: 'Netfl1x', clue: 'spelling', tell: 'The name is spelled wrong — a 1 instead of an i' },
      { t: ' support.' },
    ],
    trap: true,
    why: 'Look at the name really carefully — that is a number one, not the letter i. Somebody made a copycat name, hoping you would read it quickly.',
  },
  {
    id: 'm4',
    from: 'Ravi (your friend)',
    where: 'a chat',
    icon: '🙂',
    chunks: [
      { t: 'did you finish the maths sheet? question 4 is impossible' },
    ],
    trap: false,
    why: 'A perfectly normal message from a friend. Being careful does not mean being suspicious of everybody — that would just make you lonely.',
  },
  {
    id: 'm5',
    from: 'Game Cheats Download',
    where: 'a website',
    icon: '⬇️',
    chunks: [
      { t: 'Get unlimited coins! ' },
      { t: 'Download our special program', clue: 'download', tell: 'Downloading a program from somewhere you do not know' },
      { t: ' and ' },
      { t: 'turn off your antivirus first', clue: 'antivirus', tell: 'Asking you to switch off the thing that protects you' },
      { t: ' so it works properly.' },
    ],
    trap: true,
    why: 'Anything that asks you to switch off your protection is telling you what it is. That is the biggest warning sign there is.',
  },
  {
    id: 'm6',
    from: 'Unknown number',
    where: 'a text message',
    icon: '📱',
    chunks: [
      { t: 'Hi ' },
      { t: 'it’s your uncle, I have a new phone', clue: 'pretend', tell: 'Somebody saying who they are, with no way to check' },
      { t: '. Can you ' },
      { t: 'keep this between us', clue: 'secret', tell: 'Asking you to keep a secret from your family' },
      { t: '? I need help with something.' },
    ],
    trap: true,
    why: 'Anybody can say they are your uncle. And asking you to keep it secret is the real giveaway — it exists to stop you checking with somebody.',
  },
];

/* ------------------------------------------------------------------ */

const load = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}'); } catch { return {}; }
};
const save = (v) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch { /* private window */ }
};

export default function TrapSpotterLab() {
  const saved = useMemo(load, []);

  const [open, setOpen] = useState('m1');
  const [clues, setClues] = useState(saved.clues ?? []);
  const [picks, setPicks] = useState(saved.picks ?? {});

  useEffect(() => { document.title = 'Spot the trap · CyberCub Lab'; }, []);
  useEffect(() => { save({ clues, picks }); }, [clues, picks]);

  const msg = MESSAGES.find((m) => m.id === open);
  const myClues = msg.chunks.filter((c) => c.clue);
  const foundHere = myClues.filter((c) => clues.includes(`${msg.id}:${c.clue}`));
  const allFoundHere = foundHere.length === myClues.length;

  const judged = MESSAGES.filter((m) => picks[m.id] !== undefined);
  const right = MESSAGES.filter((m) => picks[m.id] === m.trap).length;
  const allDone = judged.length === MESSAGES.length;

  useFinished('/lab/trap-spotter', allDone);
  const safeBinned = MESSAGES.filter((m) => !m.trap && picks[m.id] === true).length;

  const takeClue = (c) => {
    const key = `${msg.id}:${c.clue}`;
    if (clues.includes(key)) return;
    setClues((v) => [...v, key]);
  };

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>Spot the trap</strong>
            <span className="lab-sub">Practical · Cyber Smart Kids, Module 2</span>
          </div>
        </div>
        <Link to="/learn/lesson/m2l1-phishing-scams-malware" className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <section className="lab-section">
        <div className="place-head">
          <div>
            <h2>Six messages. Which ones are traps?</h2>
            <p className="lab-lead">
              Click anything in a message that looks fishy. When you have found the clues, decide:
              is it a trap, or is it fine? Careful — not all of them are traps.
            </p>
          </div>
          <span className="pill">{judged.length} / {MESSAGES.length} decided</span>
        </div>

        <Tutor
          avatar="cub-sun"
          say={
            allDone
              ? safeBinned > 0
                ? `Good spotting! One thing though — you said ${safeBinned === 1 ? 'a safe message was' : 'some safe messages were'} a trap. Being careful is brilliant, but if we think everything is a trap we stop talking to our friends. Look for the tells, not just a bad feeling.`
                : 'Six out of six — including the two that were perfectly fine! That is the hard part. A good detective finds the real traps AND leaves the ordinary messages alone.'
              : allFoundHere
                ? 'You have found all the clues in this one. So what do you think — trap, or fine?'
                : `Have a really good look at this message. Click anything that seems odd. ${myClues.length > 0 ? `There ${myClues.length === 1 ? 'is 1 clue' : `are ${myClues.length} clues`} to find here.` : 'Some messages have no clues at all — they are just ordinary!'}`
          }
        />

        <div className="inbox">
          {/* ---------------- the list ---------------- */}
          <ul className="inbox-list" aria-label="Messages">
            {MESSAGES.map((m) => {
              const pick = picks[m.id];
              const done = pick !== undefined;
              const correct = pick === m.trap;
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    className={`inbox-row ${open === m.id ? 'is-open' : ''} ${done ? (correct ? 'is-right' : 'is-wrong') : ''}`}
                    onClick={() => setOpen(m.id)}
                  >
                    <span className="inbox-from">{m.icon} {m.from}</span>
                    <span className="inbox-when">{done ? (correct ? '✓' : '✗') : ''}</span>
                    <span className="inbox-subject">{m.where}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* ---------------- the message ---------------- */}
          <div className="inbox-reader">
            <h3 className="inbox-head">{msg.icon} {msg.from}</h3>
            <p className="enc-meta" style={{ marginTop: 0 }}>Seen in: {msg.where}</p>

            <p className="jr-message">
              {msg.chunks.map((c, i) =>
                c.clue ? (
                  <button
                    key={i}
                    type="button"
                    className={`jr-clue ${clues.includes(`${msg.id}:${c.clue}`) ? 'is-found' : ''}`}
                    onClick={() => takeClue(c)}
                    disabled={clues.includes(`${msg.id}:${c.clue}`)}
                  >
                    {c.t}
                  </button>
                ) : (
                  <span key={i}>{c.t}</span>
                ),
              )}
            </p>

            {myClues.length > 0 && (
              <p className="enc-meta">
                Clues found: {foundHere.length} of {myClues.length}
              </p>
            )}

            {foundHere.length > 0 && (
              <ul className="jr-tells">
                {foundHere.map((c) => (
                  <li key={c.clue}>🚩 {c.tell}</li>
                ))}
              </ul>
            )}

            {picks[msg.id] === undefined ? (
              <div className="inbox-judge">
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  onClick={() => setPicks((p) => ({ ...p, [msg.id]: false }))}
                  disabled={!allFoundHere}
                >
                  🙂 This one is fine
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setPicks((p) => ({ ...p, [msg.id]: true }))}
                  disabled={!allFoundHere}
                >
                  🚩 This is a trap
                </button>
                {!allFoundHere && (
                  <p className="enc-meta" style={{ width: '100%' }}>
                    Find all the clues first — click the fishy bits in the message.
                  </p>
                )}
              </div>
            ) : (
              <div className={`inbox-verdict ${picks[msg.id] === msg.trap ? 'is-right' : 'is-wrong'}`}>
                <strong>
                  {picks[msg.id] === msg.trap ? '✓ ' : '✗ '}
                  {msg.trap ? 'Yes — this one is a trap.' : 'No — this one is perfectly fine.'}
                </strong>
                <p>{msg.why}</p>
              </div>
            )}
          </div>
        </div>

        {allDone && (
          <div className="lab-done">
            <div>
              <strong>{right} out of {MESSAGES.length}! 🏆</strong>
              <p style={{ margin: '0.3rem 0 0' }}>
                The very best rule is the simplest one: if a message makes you feel you must hurry,
                slow down and tell a grown-up you trust. Real people never mind waiting. Traps
                always do.
              </p>
            </div>
            <Link to="/learn/lesson/m2l1-phishing-scams-malware" className="btn btn-sm">
              ← Back to the lesson
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
